import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { supabase } from '../supabase';

export default function FamilyGroup() {
  const { state } = useApp();
  const lang = state.language || 'en';
  const curr = lang==='en'?'$':lang==='da'?'DKK':lang==='sv'?'SEK':'kr';
  const fmt = (n) => lang==='en'?`$${Math.round(Math.abs(n)).toLocaleString()}`:`${Math.round(Math.abs(n)).toLocaleString(lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO')} ${curr}`;

  const [family, setFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const T = {
    title: {en:'Family & Friends',no:'Familie & venner',sv:'Familj & vänner',da:'Familie & venner'},
    noFamily: {en:'You are not in a group yet',no:'Du er ikke med i en gruppe ennå',sv:'Du är inte med i en grupp ännu',da:'Du er ikke med i en gruppe endnu'},
    create: {en:'+ Create group',no:'+ Opprett gruppe',sv:'+ Skapa grupp',da:'+ Opret gruppe'},
    join: {en:'Join',no:'Bli med',sv:'Gå med',da:'Bliv med'},
    groupName: {en:'Group name (e.g. Family Hansen)',no:'Gruppenavn (f.eks. Familie Hansen)',sv:'Gruppnamn (t.ex. Familjen Hansen)',da:'Gruppenavn (f.eks. Familie Hansen)'},
    enterCode: {en:'Enter 6-digit code',no:'Skriv inn 6-sifret kode',sv:'Ange 6-sifrig kod',da:'Indtast 6-cifret kode'},
    createBtn: {en:'Create! 🌱',no:'Opprett! 🌱',sv:'Skapa! 🌱',da:'Opret! 🌱'},
    joinBtn: {en:'Join group →',no:'Bli med →',sv:'Gå med →',da:'Bliv med →'},
    cancel: {en:'Cancel',no:'Avbryt',sv:'Avbryt',da:'Annuller'},
    invite: {en:'Invite code',no:'Invitasjonskode',sv:'Inbjudningskod',da:'Invitationskode'},
    copy: {en:'Copy',no:'Kopier',sv:'Kopiera',da:'Kopier'},
    copied: {en:'Copied! ✅',no:'Kopiert! ✅',sv:'Kopierat! ✅',da:'Kopieret! ✅'},
    saved: {en:'saved',no:'spart',sv:'sparat',da:'sparet'},
    goal: {en:'Family goal',no:'Felles mål',sv:'Familjemål',da:'Fælles mål'},
    seeMore: {en:'See details →',no:'Se detaljer →',sv:'Se detaljer →',da:'Se detaljer →'},
    leave: {en:'Leave group',no:'Forlat gruppen',sv:'Lämna gruppen',da:'Forlad gruppen'},
    leaveConfirm: {en:'Are you sure you want to leave?',no:'Er du sikker på at du vil forlate?',sv:'Är du säker på att du vill lämna?',da:lang==='en'?'Are you sure you want to leave?':lang==='sv'?'Är du säker på att du vill lämna?':lang==='da'?'Er du sikker på at du vil forlade?':'Er du sikker på at du vil forlate?'},
    members: {en:'Members',no:'Medlemmer',sv:'Medlemmar',da:'Medlemmer'},
    thisMonth: {en:'This month',no:'Denne måneden',sv:'Denna månad',da:'Denne måned'},
    noCode: {en:'Group not found. Check the code!',no:'Gruppe ikke funnet. Sjekk koden!',sv:'Gruppen hittades inte. Kontrollera koden!',da:'Gruppe ikke fundet. Tjek koden!'},
  };
  const t = (key) => T[key]?.[lang] || T[key]?.en || key;

  useEffect(() => {
    if (!state.user?.id) return;
    fetchFamily();
  }, [state.user?.id]);

  const fetchFamily = async () => {
    setLoading(true);
    try {
      // Find family membership
      const fetchUserId = state.user?.id;
      if (!fetchUserId) { setLoading(false); return; }
      const { data: membership } = await supabase
        .from('family_members')
        .select('family_id')
        .eq('user_id', fetchUserId)
        .maybeSingle();

      if (!membership) { setLoading(false); return; }

      // Get family details
      const { data: fam } = await supabase
        .from('families')
        .select('*')
        .eq('id', membership.family_id)
        .single();

      setFamily(fam);

      // Get all members with their profiles and transactions
      const { data: mems } = await supabase
        .from('family_members')
        .select('user_id, profiles(id, username, name, xp, streak)')
        .eq('family_id', membership.family_id);

      if (mems) {
        // Get this month's savings for each member
        const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
        const membersWithSavings = await Promise.all(mems.map(async (m) => {
          const { data: txs } = await supabase
            .from('transactions')
            .select('amount, cat')
            .eq('user_id', m.user_id)
            .eq('cat', 'Sparing')
            .gte('date', startOfMonth.split('T')[0]);
          const saved = (txs||[]).reduce((s,t) => s+Math.abs(t.amount), 0);
          return {
            id: m.user_id,
            name: m.profiles?.username || m.profiles?.name || 'User',
            xp: m.profiles?.xp || 0,
            streak: m.profiles?.streak || 0,
            saved,
            isMe: m.user_id === state.user.id,
          };
        }));
        setMembers(membersWithSavings.sort((a,b) => b.saved - a.saved));
      }
    } catch(e) {
      console.error(e);
    }
    setLoading(false);
  };

  const generateCode = () => Math.random().toString(36).substring(2,8).toUpperCase();

  const handleCreate = async () => {
    if (!familyName.trim()) return;
    setError('');
    try {
      const code = generateCode();
      const id = 'fam_' + Date.now();
      const userId = state.user?.id || state?.user?.id;
      if (!userId) {
        setError(lang==='en'?'Not logged in. Please refresh.':lang==='sv'?'Inte inloggad. Uppdatera sidan.':lang==='da'?'Ikke logget ind. Opdater siden.':'Ikke logget inn. Last inn siden på nytt.');
        return;
      }
      const { error: e1 } = await supabase.from('families').insert({
        id, name: familyName.trim(), code, created_by: userId
      });
      if (e1) throw e1;
      const { error: e2 } = await supabase.from('family_members').insert({
        family_id: id, user_id: userId
      });
      if (e2) throw e2;
      setShowCreate(false);
      setFamilyName('');
      fetchFamily();
    } catch(e) {
      setError(lang==='en'?`Error: ${e.message}`:lang==='sv'?`Fel: ${e.message}`:lang==='da'?`Fejl: ${e.message}`:`Feil: ${e.message}`);
      console.error('FamilyGroup error:', e);
    }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    setError('');
    try {
      const { data: fam } = await supabase
        .from('families')
        .select('id')
        .eq('code', joinCode.trim().toUpperCase())
        .single();
      if (!fam) { setError(t('noCode')); return; }
      const joinUserId = state.user?.id;
      if (!joinUserId) { setError(lang==='en'?'Not logged in':'Ikke logget inn'); return; }
      await supabase.from('family_members').insert({
        family_id: fam.id, user_id: joinUserId
      });
      setShowJoin(false);
      setJoinCode('');
      fetchFamily();
    } catch(e) {
      setError(t('noCode'));
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(t('leaveConfirm'))) return;
    await supabase.from('family_members').delete()
      .eq('family_id', family.id).eq('user_id', state.user.id);
    setFamily(null);
    setMembers([]);
  };

  if (loading) return null;

  return (
    <div style={{ marginBottom: '1rem' }}>
      {!family ? (
        <div style={{ background:'var(--surface)', borderRadius:'var(--r-lg)', padding:'0.875rem' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:8 }}>
            👨‍👩‍👧 {t('title')}
          </div>
          {!showCreate && !showJoin && (
            <>
              <div style={{ fontSize:12, color:'var(--ink3)', marginBottom:12 }}>{t('noFamily')}</div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setShowCreate(true)}
                  style={{ flex:2, padding:'10px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#22C97A,#16a34a)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                  {t('create')}
                </button>
                <button onClick={() => setShowJoin(true)}
                  style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--border)', background:'var(--card)', color:'var(--ink)', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  {t('join')}
                </button>
              </div>
            </>
          )}

          {showCreate && (
            <div>
              <input value={familyName} onChange={e=>setFamilyName(e.target.value)}
                placeholder={t('groupName')}
                style={{ width:'100%', padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)', background:'var(--card)', color:'var(--ink)', fontSize:13, marginBottom:8, boxSizing:'border-box' }}/>
              {error && <div style={{ fontSize:12, color:'var(--r400)', marginBottom:8 }}>{error}</div>}
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={handleCreate}
                  style={{ flex:2, padding:'10px', borderRadius:10, border:'none', background:'#22C97A', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                  {t('createBtn')}
                </button>
                <button onClick={() => { setShowCreate(false); setError(''); }}
                  style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--border)', background:'none', color:'var(--ink3)', fontSize:13, cursor:'pointer' }}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          )}

          {showJoin && (
            <div>
              <input value={joinCode} onChange={e=>setJoinCode(e.target.value.toUpperCase())}
                placeholder={t('enterCode')} maxLength={6}
                style={{ width:'100%', padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)', background:'var(--card)', color:'var(--ink)', fontSize:18, fontWeight:700, letterSpacing:4, textAlign:'center', marginBottom:8, boxSizing:'border-box' }}/>
              {error && <div style={{ fontSize:12, color:'var(--r400)', marginBottom:8 }}>{error}</div>}
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={handleJoin}
                  style={{ flex:2, padding:'10px', borderRadius:10, border:'none', background:'#22C97A', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                  {t('joinBtn')}
                </button>
                <button onClick={() => { setShowJoin(false); setError(''); }}
                  style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--border)', background:'none', color:'var(--ink3)', fontSize:13, cursor:'pointer' }}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ background:'linear-gradient(135deg, rgba(34,201,122,0.06), rgba(0,212,255,0.04))', border:'1px solid rgba(34,201,122,0.15)', borderRadius:'var(--r-lg)', padding:'1rem' }}>
          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)' }}>👨‍👩‍👧 {family.name}</div>
              <div style={{ fontSize:11, color:'var(--ink3)', marginTop:2 }}>{members.length} {t('members')}</div>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(family.code); setCopied(true); setTimeout(()=>setCopied(false),2000); }}
              style={{ background:'rgba(34,201,122,0.1)', border:'1px solid rgba(34,201,122,0.3)', borderRadius:8, padding:'4px 10px', color:'#22C97A', fontSize:11, fontWeight:700, cursor:'pointer' }}>
              {copied ? t('copied') : `${t('invite')}: ${family.code}`}
            </button>
          </div>

          {/* Members leaderboard */}
          {members.slice(0, showDetails ? members.length : 3).map((m, i) => (
            <div key={m.id} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, padding:'8px 10px', background: m.isMe ? 'rgba(34,201,122,0.08)' : 'rgba(255,255,255,0.02)', borderRadius:10, border: m.isMe ? '1px solid rgba(34,201,122,0.15)' : '1px solid transparent' }}>
              <div style={{ fontSize:16, width:22 }}>{['🥇','🥈','🥉','4️⃣','5️⃣','6️⃣'][i]}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight: m.isMe ? 700 : 400, color: m.isMe ? '#22C97A' : 'var(--ink)' }}>
                  {m.name}{m.isMe ? ' 👈' : ''}
                </div>
                <div style={{ fontSize:10, color:'var(--ink3)' }}>🔥 {m.streak} · ⚡ {m.xp} XP</div>
              </div>
              <div style={{ fontSize:13, fontWeight:700, color: i===0 ? '#F5C518' : 'var(--ink2)' }}>
                {fmt(m.saved)}
              </div>
            </div>
          ))}

          <div style={{ fontSize:10, color:'var(--ink3)', textAlign:'center', marginBottom:8 }}>
            📊 {t('thisMonth')}
          </div>

          {/* Felles sparemål */}
          <div style={{ background:'var(--surface)', borderRadius:'var(--r-md)', padding:'12px', marginBottom:8, border:'1px solid var(--border)' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--ink)', marginBottom:8 }}>
              🎯 {lang==='en'?'Family Goal':lang==='sv'?'Familjemål':lang==='da'?'Familiemål':'Felles sparemål'}
            </div>
            {family.shared_goal ? (
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:'var(--ink)', marginBottom:4 }}>
                  {family.shared_goal.emoji} {family.shared_goal.name}
                </div>
                <div style={{ background:'var(--border)', borderRadius:99, height:8, overflow:'hidden', marginBottom:4 }}>
                  <div style={{ width:`${Math.min(100, Math.round((family.shared_goal.saved / family.shared_goal.target) * 100))}%`, height:'100%', background:'var(--g400)', borderRadius:99 }}/>
                </div>
                <div style={{ fontSize:11, color:'var(--ink3)' }}>
                  {fmt(family.shared_goal.saved, lang)} / {fmt(family.shared_goal.target, lang)} · {Math.round((family.shared_goal.saved / family.shared_goal.target) * 100)}%
                </div>
              </div>
            ) : (
              <div style={{ fontSize:12, color:'var(--ink3)', textAlign:'center' }}>
                {lang==='en'?'No shared goal yet — set one together!':lang==='sv'?'Inget gemensamt mål ännu!':lang==='da'?'Intet fælles mål endnu!':'Ingen felles mål ennå — sett et sammen!'}
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:6 }}>
            {members.length > 3 && (
              <button onClick={() => setShowDetails(!showDetails)}
                style={{ flex:2, padding:'8px', borderRadius:10, border:'1px solid var(--border)', background:'var(--card)', color:'var(--ink)', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                {showDetails ? '▲' : t('seeMore')}
              </button>
            )}
            <button onClick={handleLeave}
              style={{ flex:1, padding:'8px', borderRadius:10, border:'1px solid rgba(255,100,100,0.2)', background:'rgba(255,50,50,0.05)', color:'var(--r400)', fontSize:11, cursor:'pointer' }}>
              {t('leave')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
